import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import useAuth from '@contexts/useAuth';
import { getInvitePreview, acceptInvitation, declineInvitation, ApiError } from '@api/organizations';
import type { InvitePreviewResponse } from '@api/organizations';
import { STORAGE_KEYS } from '@constants/index';
import { ErrorBox } from '../auth.styled';
import {
  Wrapper,
  Card,
  OrgIcon,
  Title,
  Subtitle,
  InviterText,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  SuccessText,
  LinkText,
  WarningBox,
  EmailHighlight,
} from './InviteAccept.styled';

const InviteAccept: React.FC = () => {
  const { user, logout, clearPendingInvite } = useAuth();
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = pathToken || searchParams.get('token');

  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    getInvitePreview(token)
      .then(setPreview)
      .catch((err) => {
        // 400 = invitation exists but is no longer pending (ACCEPTED, DECLINED,
        // EXPIRED, REVOKED). Redirect home instead of showing a stale page.
        // 404 = invitation not found — show the error to the user.
        if (err instanceof ApiError && err.status === 400) {
          clearPendingInvite();
          window.location.href = '/';
          return;
        }
        const message = err instanceof Error ? err.message : 'Invalid invitation';
        setError(message);
        // Invitation is no longer actionable — clear the pending invite so
        // App.tsx stops redirecting back here when the user tries to leave.
        clearPendingInvite();
      })
      .finally(() => setLoading(false));
  }, [token, clearPendingInvite]);

  // Check if the logged-in user's email matches the invitation target
  const emailMismatch =
    user && preview && user.email && preview.email &&
    user.email.toLowerCase() !== preview.email.toLowerCase();

  const handleAccept = async () => {
    if (!token || !user?.token) return;
    setProcessing(true);
    setError(null);
    try {
      await acceptInvitation(user.token, token);
      setAccepted(true);
      // Clear localStorage immediately so that if the component unmounts
      // before the redirect (e.g. due to a state-driven re-render),
      // App.tsx won't redirect back to this page.
      localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
      // Brief "Welcome!" message, then full-page redirect to reset state.
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token || !user?.token) return;
    setProcessing(true);
    setError(null);
    try {
      await declineInvitation(user.token, token);
      setDeclined(true);
      clearPendingInvite();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleSwitchAccount = async () => {
    // Store the token so the invite page reloads after re-login
    if (token) {
      localStorage.setItem(STORAGE_KEYS.PENDING_INVITE_TOKEN, token);
    }
    await logout();
    navigate('/auth/login');
  };

  const handleLoginRedirect = () => {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.PENDING_INVITE_TOKEN, token);
    }
    navigate('/auth/login');
  };

  const handleSignupRedirect = () => {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.PENDING_INVITE_TOKEN, token);
    }
    navigate('/auth/signup');
  };

  if (loading) {
    return (
      <Wrapper>
        <Card>
          <Title>Loading invitation...</Title>
        </Card>
      </Wrapper>
    );
  }

  if (error && !preview) {
    return (
      <Wrapper>
        <Card>
          <Title>Invalid Invitation</Title>
          <ErrorBox>{error}</ErrorBox>
          <LinkText>
            <Link to="/">Go to homepage</Link>
          </LinkText>
        </Card>
      </Wrapper>
    );
  }

  if (!preview) return null;

  const orgName = preview.organizationName || 'Organization';
  const orgInitial = orgName.charAt(0).toUpperCase();

  // Success states
  if (accepted) {
    return (
      <Wrapper>
        <Card>
          <OrgIcon>{orgInitial}</OrgIcon>
          <Title>Welcome!</Title>
          <SuccessText>
            You've joined <strong>{orgName}</strong>. Redirecting...
          </SuccessText>
        </Card>
      </Wrapper>
    );
  }

  if (declined) {
    return (
      <Wrapper>
        <Card>
          <Title>Invitation Declined</Title>
          <Subtitle>
            You've declined the invitation to join {orgName}.
          </Subtitle>
          <LinkText>
            <Link to="/">Go to homepage</Link>
          </LinkText>
        </Card>
      </Wrapper>
    );
  }

  // Not logged in — show login/signup prompts
  if (!user) {
    return (
      <Wrapper>
        <Card>
          <OrgIcon>{orgInitial}</OrgIcon>
          <Title>Join {orgName}</Title>
          <InviterText>
            <strong>{preview.inviterName}</strong> invited{' '}
            <EmailHighlight>{preview.email}</EmailHighlight> to join their
            organization.
          </InviterText>
          <Subtitle>
            Sign in as <EmailHighlight>{preview.email}</EmailHighlight> or
            create an account with that email to accept.
          </Subtitle>
          <ButtonRow>
            <PrimaryButton onClick={handleLoginRedirect}>Sign In</PrimaryButton>
            <SecondaryButton onClick={handleSignupRedirect}>
              Create Account
            </SecondaryButton>
          </ButtonRow>
        </Card>
      </Wrapper>
    );
  }

  // Logged in — show accept/decline (with email mismatch warning if needed)
  return (
    <Wrapper>
      <Card>
        <OrgIcon>{orgInitial}</OrgIcon>
        <Title>Join {preview.organizationName}</Title>
        <InviterText>
          <strong>{preview.inviterName}</strong> invited{' '}
          <EmailHighlight>{preview.email}</EmailHighlight> to join their
          organization.
        </InviterText>

        {emailMismatch ? (
          <>
            <WarningBox>
              You are signed in as <strong>{user.email}</strong>, but this
              invitation was sent to <strong>{preview.email}</strong>.
              <br />
              Please sign in with the correct account to accept.
            </WarningBox>
            <ButtonRow>
              <PrimaryButton onClick={handleSwitchAccount}>
                Switch Account
              </PrimaryButton>
              <SecondaryButton onClick={() => { clearPendingInvite(); navigate('/'); }}>
                Go Home
              </SecondaryButton>
            </ButtonRow>
          </>
        ) : (
          <ButtonRow>
            <PrimaryButton onClick={handleAccept} disabled={processing}>
              {processing ? 'Joining...' : 'Accept & Join'}
            </PrimaryButton>
            <SecondaryButton onClick={handleDecline} disabled={processing}>
              Decline
            </SecondaryButton>
          </ButtonRow>
        )}
        {error && !accepted && <ErrorBox>{error}</ErrorBox>}
      </Card>
    </Wrapper>
  );
};

export default InviteAccept;
